import type { ReactNode } from "react";
import type { AnswerValue } from "@ferrivia/api-contract";
import { useTranslation } from "@ferrivia/i18n";
import { useQuestionnaireCopy } from "@ferrivia/questionnaire-ui";
import { AnswerChip, AnswerChips } from "./AnswerChip";
import { useAnswerLabels } from "./useAnswerLabels";
import { isAnswered, type AnswerMap } from "./answerMap";
import { entryHasContent, followUpsAreText, type Entry, type LabelOverride } from "./summaryLayout";
import s from "./AnswerEntry.module.css";

export function AnswerBody({ value, labels }: { value: AnswerValue; labels: string[] }) {
  if (value.kind === "text") {
    return <p className={s.text}>{value.value}</p>;
  }
  return (
    <AnswerChips>
      {labels.map((label) => (
        <AnswerChip key={label}>{label}</AnswerChip>
      ))}
    </AnswerChips>
  );
}

export function QuestionBlock({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className={s.question}>
      {label && <p className={s.label}>{label}</p>}
      {children}
    </div>
  );
}

function useLabel() {
  const { t } = useTranslation("client");
  const copy = useQuestionnaireCopy();

  return (key: string, override?: LabelOverride): string | null => {
    if (override === "none") {
      return null;
    }
    if (override === "additionalInformation") {
      return t("summary.labels.additionalInformation", "Lisätiedot");
    }
    return copy.questionFor(key)?.label ?? null;
  };
}

export interface AnswerEntryProps {
  entry: Entry;
  answers: AnswerMap;
}

export function AnswerEntry({ entry, answers }: AnswerEntryProps) {
  const labelFor = useLabel();
  const labels = useAnswerLabels();

  if (!entryHasContent(entry, answers)) {
    return null;
  }

  if (entry.type === "answer") {
    const value = answers[entry.key];
    return (
      <QuestionBlock label={labelFor(entry.key, entry.label)}>
        <AnswerBody value={value} labels={labels.forQuestion(entry.key, value)} />
      </QuestionBlock>
    );
  }

  const gate = answers[entry.gate];
  const isYes = gate.kind === "boolean" && gate.value;

  if (!isYes) {
    return (
      <QuestionBlock label={labelFor(entry.gate)}>
        <AnswerBody value={gate} labels={labels.forQuestion(entry.gate, gate)} />
      </QuestionBlock>
    );
  }

  return (
    <QuestionBlock label={labelFor(entry.gate)}>
      {followUpsAreText(entry) && <AnswerBody value={gate} labels={labels.forQuestion(entry.gate, gate)} />}
      {entry.followUps.map((key) => {
        const value = answers[key];
        return isAnswered(value) ? <AnswerBody key={key} value={value} labels={labels.forQuestion(key, value)} /> : null;
      })}
    </QuestionBlock>
  );
}
